import type { TicketStatusCondition as TicketStatusConditionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { TicketStatusOption } from "@redotech/redo-model/advanced-flow/rules/schemas";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { RedoSingleSelectDropdownInputButton } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";

const STATUS_DISPLAY_NAMES: Record<TicketStatusOption, string> = {
  [TicketStatusOption.OPEN]: "Open",
  [TicketStatusOption.CLOSED]: "Closed",
  [TicketStatusOption.SNOOZED]: "Snoozed",
  [TicketStatusOption.IN_PROGRESS]: "In progress",
};

const statusItems: RedoListItem<TicketStatusOption>[] = Object.values(
  TicketStatusOption,
).map((status) => ({
  id: status,
  type: "text" as const,
  text: STATUS_DISPLAY_NAMES[status],
  value: status,
}));

export const TicketStatusCondition = memo(function TicketStatusCondition({
  condition,
  onChange,
}: {
  condition: TicketStatusConditionType;
  onChange: (updated: TicketStatusConditionType) => void;
}) {
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems: RedoListItem<TicketStatusOption>[] = useMemo(() => {
    return (condition.statuses || []).map((status) => ({
      id: status,
      type: "text" as const,
      text: STATUS_DISPLAY_NAMES[status],
      value: status,
    }));
  }, [condition.statuses]);

  const displayItem: RedoListItem<TicketStatusOption> | undefined =
    useMemo(() => {
      if (selectedItems.length === 0) {
        return undefined;
      }
      if (selectedItems.length === 1) {
        return selectedItems[0];
      }
      return {
        id: "display",
        type: "text" as const,
        text: `${selectedItems.length} statuses selected`,
        value: TicketStatusOption.OPEN,
      };
    }, [selectedItems]);

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text fontSize="xs" textColor="tertiary">
        Status
      </Text>
      <RedoSingleSelectDropdownInputButton
        isDropdownOpen={dropdownOpen}
        onClick={() => setDropdownOpen(!dropdownOpen)}
        placeholder="Select statuses"
        ref={setDropdownButtonRef}
        selectedItem={displayItem}
        size="xs"
      />
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={statusItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          onChange({ ...condition, statuses: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
